'use client';

import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  PiArrowLeft, PiCaretDown, PiMagnifyingGlass, PiMapPin, PiX,
} from 'react-icons/pi';
import EventDTO from '../../models/event';
import {
  DATE_CHIPS, DateChip, EventSearchFilter, eventVenueName, searchEvents, searchGroups,
} from '../../utils/eventSearch';
import SearchResults from './SearchResults';

interface HomeSearchProps {
  /** Public events of the home, already sorted (featured first). */
  events: EventDTO[];
  onEventSelect: (slug: string) => void;
  /** "Ver todos": the page filters its grid with this filter. */
  onShowAll: (filter: EventSearchFilter) => void;
}

const MAX_DESKTOP_EVENTS = 5;
const MAX_MOBILE_EVENTS = 8;

const chipClass = (active: boolean) => (
  `flex-shrink-0 inline-flex items-center h-10 px-4 rounded-full border text-sm font-medium whitespace-nowrap transition-colors ${
    active
      ? 'bg-primary-500 text-white border-primary-500'
      : 'bg-surface-base text-text-primary border-border-primary/50 hover:border-primary-300 hover:text-primary-500'
  }`
);

const HomeSearch = ({ events, onEventSelect, onShowAll }: HomeSearchProps) => {
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState<DateChip | undefined>();
  const [province, setProvince] = useState<string | undefined>();
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [mounted, setMounted] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setMounted(true), []);

  const provinces = useMemo(
    () => Array.from(new Set(events.map((e) => e.province).filter((p): p is string => !!p)))
      .sort((a, b) => a.localeCompare(b, 'es')),
    [events],
  );

  const filter: EventSearchFilter = useMemo(() => ({ query, chip, province }), [query, chip, province]);
  const active = query.trim().length > 0 || !!chip;
  const chipResults = useMemo(() => searchEvents(events, filter), [events, filter]);
  // A date chip left on must not hide the event someone typed: when the chip
  // leaves nothing but the text matches other dates, show those instead.
  const anyDateFilter: EventSearchFilter = useMemo(() => ({ query, province }), [query, province]);
  const anyDateResults = useMemo(
    () => (chip && query.trim() && chipResults.length === 0 ? searchEvents(events, anyDateFilter) : []),
    [events, anyDateFilter, chip, query, chipResults],
  );
  const usingAnyDate = anyDateResults.length > 0;
  const effectiveFilter = usingAnyDate ? anyDateFilter : filter;
  const results = usingAnyDate ? anyDateResults : chipResults;
  const venues = useMemo(() => searchGroups(events, effectiveFilter, eventVenueName, true), [events, effectiveFilter]);
  const producers = useMemo(() => searchGroups(events, effectiveFilter, (e) => e.business_name), [events, effectiveFilter]);
  const featured = useMemo(() => events.slice(0, 3), [events]);
  const chipLabel = DATE_CHIPS.find((c) => c.id === chip)?.label;
  const count = results.length === 1 ? '1 evento' : `${results.length} eventos`;
  let eventsTitle = `Eventos · ${count}`;
  if (usingAnyDate) {
    eventsTitle = `En otras fechas · ${count}`;
  } else if (chipLabel && !query.trim()) {
    eventsTitle = `${chipLabel} · ${count}`;
  }

  useEffect(() => setActiveIndex(-1), [filter]);
  // Reopening must not resume the keyboard cursor from the last time.
  useEffect(() => {
    if (!open) { setActiveIndex(-1); }
  }, [open]);

  // Desktop: close the dropdown when clicking outside the search block.
  useEffect(() => {
    if (!open) { return undefined; }
    const onDown = (ev: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(ev.target as Node)) { setOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Mobile: full-screen sheet locks the page scroll and focuses the input.
  useEffect(() => {
    if (!mobileOpen) { return undefined; }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => mobileInputRef.current?.focus(), 60);
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen]);

  const closeAll = () => {
    setOpen(false);
    setMobileOpen(false);
    desktopInputRef.current?.blur();
  };

  const selectEvent = (e: EventDTO) => {
    closeAll();
    onEventSelect(e.slug || e.id);
  };

  const showAll = () => {
    if (!active) { return; }
    closeAll();
    onShowAll({ query: query.trim(), chip: usingAnyDate ? undefined : chip, province });
  };

  const toggleChip = (id: DateChip) => {
    setChip((c) => (c === id ? undefined : id));
    setOpen(true);
  };

  const pickGroup = (name: string) => {
    setQuery(name);
    setOpen(true);
    (mobileOpen ? mobileInputRef : desktopInputRef).current?.focus();
  };

  const clear = () => {
    setQuery('');
    setChip(undefined);
    (mobileOpen ? mobileInputRef : desktopInputRef).current?.focus();
  };

  const onDesktopKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    const shown = results.slice(0, MAX_DESKTOP_EVENTS);
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, shown.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (ev.key === 'Escape') {
      // type="search" clears itself on Esc, which would reopen the dropdown.
      ev.preventDefault();
      setOpen(false);
      desktopInputRef.current?.blur();
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      if (activeIndex >= 0 && shown[activeIndex]) { selectEvent(shown[activeIndex]); } else showAll();
    }
  };

  const provinceSelect = (id: string, compact: boolean) => (provinces.length > 1 ? (
    <span className={`relative flex items-center gap-2 flex-shrink-0 ${compact ? 'h-10 pl-3 pr-2 rounded-full border border-border-secondary' : ''}`}>
      <PiMapPin className={`${compact ? 'text-base' : 'text-xl'} text-primary-500`} aria-hidden="true" />
      <label htmlFor={id} className="sr-only">Provincia</label>
      <select
        id={id}
        value={province ?? ''}
        onChange={(e) => { setProvince(e.target.value || undefined); setOpen(true); }}
        className={`appearance-none bg-transparent border-0 outline-none cursor-pointer text-text-primary pr-6 ${
          compact ? 'text-sm font-medium' : 'text-sm-medium max-w-[200px]'
        }`}
      >
        <option value="">Todas las provincias</option>
        {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <PiCaretDown className={`absolute ${compact ? 'right-2.5' : 'right-0'} pointer-events-none text-text-secondary`} aria-hidden="true" />
    </span>
  ) : null);

  const notice = usingAnyDate && chipLabel ? {
    text: `${chipLabel} no hay eventos para “${query.trim()}”. Encontramos estos en otras fechas.`,
    actionLabel: `Quitar filtro “${chipLabel}”`,
    onAction: () => setChip(undefined),
  } : undefined;

  const resultsProps = {
    notice,
    active,
    query,
    eventsTitle,
    results,
    venues,
    producers,
    featured,
    onSelectEvent: selectEvent,
    onPickGroup: pickGroup,
    onShowAll: showAll,
    onClear: clear,
  };

  return (
    <>
      {/* Desktop: one row, full content width (aligned with the banner).
          z-[45]: above its backdrop (z-40) but under the fixed header (z-50),
          which it would otherwise paint over on scroll since it comes later in the DOM. */}
      <section ref={wrapperRef} className="hidden lg:flex items-center gap-3 relative z-[45]" aria-label="Buscador de eventos">
        <h2 className="sr-only">Buscar eventos</h2>
        <div className="relative flex-1 min-w-0">
          <form
            role="search"
            onSubmit={(e) => { e.preventDefault(); showAll(); }}
            className="flex items-center gap-3 h-14 pl-5 pr-2 rounded-full border border-border-primary/50 bg-surface-base shadow-sm transition-shadow focus-within:border-primary-500 focus-within:ring-4 focus-within:ring-primary-500/20"
          >
            <PiMagnifyingGlass className="text-[22px] text-text-secondary flex-shrink-0" aria-hidden="true" />
            <label htmlFor="home-search-desktop" className="sr-only">Buscar eventos</label>
            <input
              id="home-search-desktop"
              ref={desktopInputRef}
              type="search"
              autoComplete="off"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onDesktopKeyDown}
              placeholder="¿A qué evento vas? Buscá por evento, artista, lugar o productora"
              className="flex-1 min-w-0 bg-transparent border-0 outline-none text-md-regular text-text-primary placeholder:text-text-tertiary [&::-webkit-search-cancel-button]:hidden"
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); desktopInputRef.current?.focus(); }}
                aria-label="Borrar búsqueda"
                className="w-8 h-8 rounded-full bg-surface-secondary inline-flex items-center justify-center text-text-secondary flex-shrink-0"
              >
                <PiX aria-hidden="true" />
              </button>
            )}
            {provinces.length > 1 && <span className="w-px h-7 bg-border-primary/40 flex-shrink-0" aria-hidden="true" />}
            {provinceSelect('home-search-province', false)}
            <button
              type="submit"
              className="h-10 px-6 rounded-full bg-primary-500 hover:bg-primary-700 text-white text-md-semibold flex-shrink-0 transition-colors"
            >
              Buscar
            </button>
          </form>

          {open && (
            <div className="absolute top-[68px] left-0 right-0 rounded-3xl border border-border-primary/30 bg-surface-base shadow-xl overflow-hidden">
              <SearchResults
                variant="desktop"
                maxEvents={MAX_DESKTOP_EVENTS}
                activeIndex={activeIndex}
                onHoverIndex={setActiveIndex}
                {...resultsProps}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0" role="group" aria-label="Filtros rápidos por fecha">
          {DATE_CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-pressed={chip === c.id}
              onClick={() => toggleChip(c.id)}
              className={`${chipClass(chip === c.id)} !h-14 !px-5`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {open && (
        <button
          type="button"
          tabIndex={-1}
          aria-label="Cerrar búsqueda"
          onClick={() => setOpen(false)}
          className="hidden lg:block fixed inset-0 z-40 bg-surface-overlay/25 cursor-default border-0 p-0"
        />
      )}

      {/* Mobile */}
      <section className="lg:hidden flex flex-col gap-3" aria-label="Buscador de eventos">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex items-center gap-3 w-full h-[54px] px-5 rounded-full border border-border-primary/50 bg-surface-base shadow-sm text-md-regular text-text-tertiary text-left"
        >
          <PiMagnifyingGlass className="text-[22px] text-text-primary flex-shrink-0" aria-hidden="true" />
          Buscá evento, artista o lugar
        </button>
        <div className="flex gap-2 overflow-x-auto -mx-2 px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {DATE_CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { setQuery(''); setChip(c.id); setMobileOpen(true); }}
              className={chipClass(false)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {mounted && mobileOpen && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Buscar eventos"
          className="lg:hidden fixed inset-0 z-[1000] bg-surface-base flex flex-col font-primary"
        >
          <div className="flex items-center gap-1 pt-2.5 pl-2 pr-4 flex-shrink-0">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="Volver"
              className="w-11 h-11 inline-flex items-center justify-center text-text-primary flex-shrink-0"
            >
              <PiArrowLeft className="text-[22px]" aria-hidden="true" />
            </button>
            <form
              role="search"
              onSubmit={(e) => { e.preventDefault(); mobileInputRef.current?.blur(); }}
              className="flex-1 min-w-0 flex items-center gap-2.5 h-[50px] pl-4 pr-2 rounded-full border border-primary-500 ring-4 ring-primary-500/20"
            >
              <PiMagnifyingGlass className="text-xl text-text-secondary flex-shrink-0" aria-hidden="true" />
              <label htmlFor="home-search-mobile" className="sr-only">Buscar eventos</label>
              <input
                id="home-search-mobile"
                ref={mobileInputRef}
                type="search"
                enterKeyHint="search"
                autoComplete="off"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Evento, artista o lugar"
                className="flex-1 min-w-0 bg-transparent border-0 outline-none text-base text-text-primary placeholder:text-text-tertiary [&::-webkit-search-cancel-button]:hidden"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); mobileInputRef.current?.focus(); }}
                  aria-label="Borrar búsqueda"
                  className="w-8 h-8 rounded-full bg-surface-secondary inline-flex items-center justify-center text-text-secondary flex-shrink-0"
                >
                  <PiX className="text-sm" aria-hidden="true" />
                </button>
              )}
            </form>
          </div>

          <div className="flex gap-2 overflow-x-auto px-4 pt-3.5 pb-3 border-b border-border-secondary flex-shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {DATE_CHIPS.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={chip === c.id}
                onClick={() => toggleChip(c.id)}
                className={chipClass(chip === c.id)}
              >
                {c.label}
              </button>
            ))}
            {provinceSelect('home-search-province-mobile', true)}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            <SearchResults variant="mobile" maxEvents={MAX_MOBILE_EVENTS} {...resultsProps} />
          </div>

          {active && results.length > MAX_MOBILE_EVENTS && (
            <div className="px-4 pt-3 pb-5 border-t border-border-secondary flex-shrink-0">
              <button
                type="button"
                onClick={showAll}
                className="w-full h-12 rounded-full bg-primary-500 text-white text-md-bold"
              >
                {`Ver los ${results.length} resultados`}
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

export default HomeSearch;
